import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import type { AudioLevelProbe } from "@/store/audio-capture/interfaces/audio-level-probe";
import { WebAnalyserProbe } from "../visualizers/web-analyser-probe";

export interface MicrophoneCaptureResult {
  stream: MediaStream;
  analyser: AnalyserNode;
  audioContext: AudioContext;
  visualizer: AudioLevelProbe;
}

export class MicrophoneCaptureService {
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private visualizer: AudioLevelProbe | null = null;
  private stream: MediaStream | null = null;

  async start({
    deviceId,
    sampleRate,
  }: {
    deviceId?: string;
    sampleRate?: number;
  } = {}): Promise<MicrophoneCaptureResult> {
    if (this.stream) {
      return {
        stream: this.stream,
        analyser: this.analyser!,
        audioContext: this.audioContext!,
        visualizer: this.visualizer!,
      };
    }

    const constraints = navigator.mediaDevices.getSupportedConstraints();
    logger.info({
      message: "[MicrophoneCaptureService] Supported constraints",
      info: {
        constraints: objectModule.safeAsJson(constraints as Record<string, boolean | undefined>),
      },
    });

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId,
        sampleRate,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    } as MediaStreamConstraints);

    logger.info({
      message: "[MicrophoneCaptureService] Obtained microphone stream",
      info: {
        id: this.stream.id,
        active: this.stream.active,
      },
    });

    this.audioContext = new AudioContext({
      sampleRate,
      latencyHint: "interactive",
    });
    this.analyser = this.audioContext.createAnalyser();

    const source = this.audioContext.createMediaStreamSource(this.stream);
    source.connect(this.analyser);

    this.visualizer = new WebAnalyserProbe(this.analyser);

    return {
      stream: this.stream,
      analyser: this.analyser,
      audioContext: this.audioContext,
      visualizer: this.visualizer,
    };
  }

  async stop(): Promise<void> {
    if (this.visualizer) {
      this.visualizer.dispose();
      this.visualizer = null;
    }

    if (this.analyser) {
      this.analyser.disconnect();
      this.analyser = null;
    }

    if (this.audioContext) {
      await this.audioContext.close().catch(() => undefined);
      this.audioContext = null;
    }

    if (this.stream) {
      for (const track of this.stream.getTracks()) {
        track.stop();
      }
      this.stream = null;
    }
  }
}
